// "Your AI journey": the Champions program on the dashboard, about the viewer.
// Their level (with the ladder of levels a hover or click away), badges and
// next step, their champions, and where their community stands in the AI
// Champions Cup (the full standings are on the Champions page).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Check, Heart, Lightbulb, Lock, Plus, Rocket, Sparkles, Star, Trophy } from "lucide-react";
import { useChampionsConfig } from "../../hooks/useChampionsConfig";
import { useChampionDirectory } from "../../hooks/useChampionDirectory";
import { useContributions } from "../../hooks/useContributions";
import { communityForDepartment, type CommunityId } from "../../data/championsDirectory";
import { LEVELS, POINTS, badgesFor, levelFor, rankContributors, type Contributor } from "../../lib/contributions";
import { Face } from "../champions/parts";
import { displayName } from "../../lib/contributions";
import { PointsGuide } from "../champions/PointsGuide";
import { standingOf, useCupStandings } from "../champions/ChampionsCup";

const BADGE_ICONS = { idea: Lightbulb, tool: Sparkles, live: Rocket, likes: Heart } as const;

/** A small panel that opens on hover, or stays open on click, beside the
 *  thing it explains. Escape or a click elsewhere closes it. */
function Pop({ trigger, label, className, children }: { trigger: ReactNode; label: string; className?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const wrap = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !wrap.current?.contains(e.target as Node)) {
        pinned.current = false;
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <span
      ref={wrap}
      className={`aj-pop-wrap${className ? ` ${className}` : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => !pinned.current && setOpen(false)}
    >
      <button
        type="button"
        className="aj-pop-trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          pinned.current = !open || !pinned.current;
          setOpen(pinned.current);
        }}
      >
        {trigger}
      </button>
      {open ? (
        <span className="aj-pop" role="dialog" aria-label={label}>
          {children}
        </span>
      ) : null}
    </span>
  );
}

/** Every level, with the viewer's place on the way. */
function LevelsList({ points, onHowPoints }: { points: number; onHowPoints: () => void }) {
  const level = levelFor(points);
  return (
    <>
      <span className="aj-pop-title">AI levels</span>
      <ol className="aj-pop-levels">
        {LEVELS.map((l, i) => {
          const n = i + 1;
          const state = n < level.number ? "done" : n === level.number ? "now" : "later";
          return (
            <li key={l.name} className={`aj-pop-level aj-pop-level--${state}`}>
              <span className="aj-pop-num" aria-hidden>
                {state === "done" ? <Check size={12} /> : n}
              </span>
              <span className="aj-pop-name">{l.name}</span>
              <span className="aj-pop-from">{l.from ? `${l.from}+ pts` : "Start"}</span>
              {state === "now" ? <span className="aj-pop-you">You</span> : null}
            </li>
          );
        })}
      </ol>
      <button type="button" className="cup-how" onClick={onHowPoints}>
        How points work
      </button>
    </>
  );
}

/** What the viewer's points are made of. */
function PointsBreakdown({ me, onHowPoints }: { me: Contributor | undefined; onHowPoints: () => void }) {
  const tools = me ? me.tools.Prompt + me.tools.Agent + me.tools.App + me.tools.Other : 0;
  const rows = me
    ? [
        { label: `${me.ideas} idea${me.ideas === 1 ? "" : "s"} submitted`, each: POINTS.idea, n: me.ideas },
        { label: `${me.building} being built`, each: POINTS.building, n: me.building },
        { label: `${me.live} gone live`, each: POINTS.building + POINTS.live, n: me.live },
        { label: `${tools} tool${tools === 1 ? "" : "s"} shared`, each: POINTS.tool, n: tools },
      ].filter((r) => r.n)
    : [];
  const listed = rows.reduce((t, r) => t + r.n * r.each, 0);
  const likes = (me?.points ?? 0) - listed;
  return (
    <>
      <span className="aj-pop-title">Your points</span>
      {rows.length ? (
        <span className="aj-pts">
          {rows.map((r) => (
            <span key={r.label} className="aj-pts-row">
              <span>{r.label}</span>
              <span className="aj-pts-calc">
                {r.n} × {r.each}
              </span>
              <b>{r.n * r.each}</b>
            </span>
          ))}
          {likes > 0 ? (
            <span className="aj-pts-row">
              <span>{me?.likes} likes on your tools</span>
              <span className="aj-pts-calc">+1 per 5</span>
              <b>{likes}</b>
            </span>
          ) : null}
          <span className="aj-pts-row aj-pts-total">
            <span>Total</span>
            <span />
            <b>{me?.points ?? 0}</b>
          </span>
        </span>
      ) : (
        <span className="aj-pop-empty">Nothing yet. Submit an idea or share a prompt to earn your first points.</span>
      )}
      <button type="button" className="cup-how" onClick={onHowPoints}>
        How points work
      </button>
    </>
  );
}

export function ChampionsStrip({
  email,
  department,
  name,
  title,
  photoUrl,
}: {
  email?: string;
  department?: string;
  /** The viewer, shown on their level tile. */
  name?: string;
  title?: string;
  photoUrl?: string | null;
}) {
  const { config } = useChampionsConfig();
  const people = useChampionDirectory(config.champions);
  const { data } = useContributions();
  const cup = useCupStandings(config.communities, config.leaderboardExclude);
  const [guide, setGuide] = useState(false);
  const closeGuide = useCallback(() => setGuide(false), []);
  // The progress bar fills in once the points arrive.
  const [grow, setGrow] = useState(false);
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => setGrow(true), 60);
    return () => clearTimeout(t);
  }, [data]);

  const mail = email?.toLowerCase() ?? "";
  const me = people.find((p) => mail && p.email?.toLowerCase() === mail);
  const mine: CommunityId | null = me?.community ?? communityForDepartment(department);
  const myChampions = people.filter((p) => p.community === mine && p.name !== me?.name);
  const faces = [...(mine ? myChampions : people)].sort((a, b) => Number(!!b.photoUrl) - Number(!!a.photoUrl)).slice(0, 4);
  const nameOf = (id: CommunityId) => config.communities.find((c) => c.id === id)?.name ?? id;

  const myself = useMemo(
    () => (data && mail ? rankContributors(data.initiatives, data.library).find((c) => c.email === mail) : undefined),
    [data, mail]
  );
  const points = myself?.points ?? 0;
  const level = levelFor(points);
  const badges = badgesFor(myself);
  const earned = badges.filter((b) => b.earned).length;
  const standing = standingOf(cup, mine);

  return (
    <section className="db-card aj" aria-labelledby="aj-title">
      <div className="aj-head">
        <h2 id="aj-title" className="ch-kicker">
          Your AI journey
        </h2>
        <Link className="cup-champs" to={mine ? `/champions-new?community=${mine}` : "/champions-new"}>
          <span className="cup-faces" aria-hidden>
            {faces.map((p) => (
              <Face key={p.name} name={p.name} photoUrl={p.photoUrl} size={30} />
            ))}
          </span>
          <span>{me ? "Your fellow champions" : mine ? "Meet your champions" : "Meet the champions"}</span>
          <ArrowRight size={14} aria-hidden />
        </Link>
      </div>

      <div className="aj-body">
        {/* Level */}
        <div className="aj-level">
          <div className="aj-me">
            <span className={`aj-avatar aj-medal--${level.number}`}>
              <Face name={displayName(name ?? "") || "You"} photoUrl={photoUrl ?? null} size={56} />
              <span className="aj-avatar-level" aria-hidden>
                {level.number}
              </span>
            </span>
            <span className="aj-me-text">
              <span className="aj-me-name">
                {displayName(name ?? "") || "You"}
                {me ? (
                  <span className="cup-champ-tag" title="AI Champion">
                    <Award size={11} aria-hidden /> Champion
                  </span>
                ) : null}
              </span>
              {title ? <span className="aj-me-title">{title}</span> : null}
              <Pop
                label="AI levels"
                className={`aj-level-pill aj-medal--${level.number}`}
                trigger={
                  <>
                    <Star size={11} fill="currentColor" aria-hidden /> Level {level.number} · {level.name}
                  </>
                }
              >
                <LevelsList points={points} onHowPoints={() => setGuide(true)} />
              </Pop>
            </span>
          </div>
          <div className="aj-progress">
            <span className="aj-progress-bar" aria-hidden>
              <span style={{ width: grow ? `${level.next ? Math.max(4, level.progress * 100) : 100}%` : "0%" }} />
            </span>
            <span className="aj-progress-text">
              <Pop label="Your points" className="aj-pts-pop" trigger={<b>{points} pts</b>}>
                <PointsBreakdown me={myself} onHowPoints={() => setGuide(true)} />
              </Pop>
              {level.next ? ` · ${level.next.from - points} more to ${level.next.name}` : " · top level reached"}
            </span>
          </div>
        </div>

        {/* Badges */}
        <div className="aj-badges">
          <p className="aj-label">
            Badges <span>{earned} of {badges.length}</span>
          </p>
          <ul className="aj-medallions">
            {badges.map((b) => {
              const Icon = BADGE_ICONS[b.id as keyof typeof BADGE_ICONS];
              return (
                <li key={b.id} className={`aj-medallion${b.earned ? " is-earned" : ""}`} title={b.earned ? b.label : `${b.label}: ${b.hint}`}>
                  <span className="aj-medallion-disc" aria-hidden>
                    <Icon size={18} />
                    {!b.earned ? (
                      <span className="aj-medallion-lock">
                        <Lock size={9} />
                      </span>
                    ) : null}
                  </span>
                  <span className="aj-medallion-name">{b.label}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Where your community stands */}
        <div className="aj-cup">
          <Link className="aj-rank" to="/champions-new#champions-cup">
            <span className="aj-rank-tile" aria-hidden>
              <Trophy size={14} />
              <b>{standing ? `#${standing.rank}` : "–"}</b>
            </span>
            <span className="aj-rank-text">
              <span className="aj-rank-kicker">Champions Cup · {cup?.label ?? "this quarter"}</span>
              <span className="aj-rank-name">{mine ? nameOf(mine) : "Your community"}</span>
              <span className="aj-rank-sub">
                {!standing
                  ? "Every idea you add earns points for your team."
                  : standing.ahead
                    ? `${standing.gap} pt${standing.gap === 1 ? "" : "s"} behind ${nameOf(standing.ahead)} · ${standing.ideas} idea${standing.ideas === 1 ? "" : "s"} to pass`
                    : "Leading the Cup. Keep it up!"}
              </span>
            </span>
            <ArrowRight className="aj-rank-go" size={15} aria-hidden />
          </Link>
          <Link className="ch-btn ch-btn--primary cup-go" to="/initiatives?new=1">
            <Plus size={15} aria-hidden /> Submit an idea <span className="cup-plus">+{POINTS.idea} pts</span>
          </Link>
        </div>
      </div>
      {guide ? <PointsGuide points={points} onClose={closeGuide} /> : null}
    </section>
  );
}
